/* eslint-disable max-lines */
import axios from 'axios';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import actions from 'state/actions';
import selectors from 'state/selectors';
import { useLocation } from 'react-router-dom';
import chatbotService from 'services/chatbot.service';
import Tesseract from 'tesseract.js';
import services from 'services';
import { UserCompany } from 'models/response';
import { moduleProgramCode } from 'utils/constants';
import html2canvas from 'html2canvas';

let lastLucyOcr: string | null = null;

if (typeof window !== 'undefined') {
  (window as any).__lastLucyOcr = lastLucyOcr;
}

const useChatbot = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const userId = useSelector(selectors.user.getIdUser) || -1;
  const programId = useSelector(selectors.app.getProgramCode) || -1;

  // Always use company id 0 for chatbot
  const companyId = 0;
  const chatbotAdvancedConfigAction = 'ChatbotAdvancedConfig';

  const abortControllerRef = useRef<AbortController | null>(null);

  const sessionId = useSelector(selectors.chatbot.getSessionId);
  const messages = useSelector(selectors.chatbot.getMessages);
  const historyMessages = useSelector(selectors.chatbot.getHistoryMessages);
  const loading = useSelector(selectors.chatbot.getLoading);
  const hasChatbotAdvancedConfig = useSelector(selectors.chatbot.getHasChatbotAdvancedConfig);

  const mode = useSelector(selectors.chatbot.getDisplayMode);

  const selectedCompany = useSelector(selectors.configurator.getSelectedCompany);

  const activeDocumentId = useSelector(selectors.documents.selectActiveDocumentID);
  const documentImageId = useSelector(selectors.documents.selectDocumentImageId);

  const configuratorState = useSelector((state: any) => state.configurator);

  const isShowen = useSelector(selectors.chatbot.getIsOpen);

  const close = () => dispatch(actions.chatbot.setOpen(false));

  const [isCheckingPrivileges, setIsCheckingPrivileges] = useState<boolean>(true);

  const checkPrivileges = () => {
    const checkChatbotPrivileges = async () => {
      try {
        setIsCheckingPrivileges(true);
        const response = await services.getConfigurationsByCompanies(userId, moduleProgramCode.chatbot);
        const userCompanies: UserCompany[] = response.data;

        const hasPrivilege = userCompanies.some(
          ({ idCompany, actions }) =>
            idCompany === companyId && actions.find(({ type }) => type === chatbotAdvancedConfigAction),
        );
        dispatch(actions.chatbot.setHasChatbotAdvancedConfig(hasPrivilege));
      } catch (error) {
        console.error('Error checking chatbot privileges:', error);
        dispatch(actions.chatbot.setHasChatbotAdvancedConfig(false));
      } finally {
        setIsCheckingPrivileges(false);
      }
    };

    if (userId) {
      checkChatbotPrivileges();
    } else {
      setIsCheckingPrivileges(false);
      dispatch(actions.chatbot.setHasChatbotAdvancedConfig(false));
    }
  };

  const abort = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    dispatch(actions.chatbot.updateLastMessage(''));
  };

  const clearMessages = () => {
    abort();
    dispatch(actions.chatbot.clearMessages());
  };

  const getPageContextAsString = () => {
    const contextParts = [];

    contextParts.push(`path:${location.pathname}`);

    selectedCompany?.name && contextParts.push(`company:${selectedCompany.name}`);
    activeDocumentId && contextParts.push(`activeDoc:${activeDocumentId}`);
    documentImageId && contextParts.push(`docImage:${documentImageId}`);

    const dropdownValues: string[] = [];

    if (configuratorState.companies?.selectedCompany?.name) {
      dropdownValues.push(`selectedCompany:${configuratorState.companies.selectedCompany.name}`);
    }

    if (configuratorState.mapper?.activeDocTypeId) {
      dropdownValues.push(`docType:${configuratorState.mapper.activeDocTypeId}`);
    }

    if (dropdownValues.length > 0) {
      contextParts.push(`dropdowns:${dropdownValues.join(',')}`);
    }

    contextParts.push(`time:${new Date().toISOString()}`);

    return contextParts.join('|');
  };

  // === OCR ===
  const state = {
    worker: null as Tesseract.Worker | null,
    lastText: '',
    lastMd: '',
  };

  const ensureWorker = async () => {
    if (state.worker) return state.worker;
    state.worker = await Tesseract.createWorker('eng', 1, { legacyCore: false });
    return state.worker;
  };

  const grabPageCanvas = () => {
    return html2canvas(document.body, {
      useCORS: true,
      logging: false,
      scale: 1,
    });
  };

  const runOcrOnce = async (): Promise<void> => {
    try {
      const w = await ensureWorker();
      const canvas = await grabPageCanvas();
      const { data } = await w.recognize(canvas);
      const text = data?.text || '';
      const ts = new Date().toISOString();
      if (text) {
        state.lastText = text;
        state.lastMd = ['## Snapshot OCR (Lucy iframe) — ' + ts, '', '```text', text, '```'].join('\n');
        lastLucyOcr = state.lastMd;
        if (typeof window !== 'undefined') {
          (window as any).__lastLucyOcr = state.lastMd;
        }
      }
    } catch (error) {
      console.error('OCR error', error);
    }
  };

  const selectedModel = useSelector(selectors.chatbot.getSelectedModal);
  const setSelectedModel = (model: string) => {
    dispatch(actions.chatbot.setSelectedModel(model));
  };

  const useNeighbors = useSelector(selectors.chatbot.getUseNeighbors);
  const setUseNeighbors = (useNeighbors: boolean) => {
    dispatch(actions.chatbot.setUseNeighbors(useNeighbors));
  };

  const temperature = useSelector(selectors.chatbot.getTemperature);
  const setTemperature = (temperature: number) => {
    dispatch(actions.chatbot.setTemperature(temperature));
  };

  const suppressHistory = useSelector(selectors.chatbot.getSuppressHistory);
  const setSuppressHistory = (suppressHistory: boolean) => {
    dispatch(actions.chatbot.setSuppressHistory(suppressHistory));
  };

  const toolsEnabled = useSelector(selectors.chatbot.getToolsEnabled);
  const setToolsEnabled = (toolsEnabled: boolean) => {
    dispatch(actions.chatbot.setToolsEnabled(toolsEnabled));
  };

  const suppressContext = useSelector(selectors.chatbot.getSuppressContext);
  const setSuppressContext = (suppressContext: boolean) => {
    dispatch(actions.chatbot.setSuppressContext(suppressContext));
  };

  const onlyValidated = useSelector(selectors.chatbot.getOnlyValidated);
  const setOnlyValidated = (onlyValidated: boolean) => {
    dispatch(actions.chatbot.setOnlyValidated(onlyValidated));
  };

  const tools = useSelector(selectors.chatbot.getTools);
  const models = useSelector(selectors.chatbot.getModels);
  const feedbackStates = useSelector(selectors.chatbot.getFeedbackStates);

  const fetchInitialData = async () => {
    try {
      const models = await chatbotService.fetchModels();
      dispatch(actions.chatbot.setModels(models));

      const tools = await chatbotService.fetchTools();
      dispatch(actions.chatbot.setTools(tools));
    } catch (error) {
      console.error('Failed to fetch chatbot data:', error);
    } finally {
      const defaultModel = 'gemini-2.5-flash';
      dispatch(actions.chatbot.setSelectedModel(defaultModel));
    }
  };

  const ask = async (question: string) => {
    if (!question) return;
    dispatch(actions.chatbot.setLoading(true));
    dispatch(actions.chatbot.addMessage({ question, answer: null }));

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      runOcrOnce();

      const pageContext = getPageContextAsString();
      const ocrData = (window as any).__lastLucyOcr || lastLucyOcr || '';

      const clientContextWithOCR = ocrData ? `${pageContext}|ocr:${ocrData}` : pageContext;
      const response = await chatbotService.sendChatMessageMultimodal(
        {
          query: question,
          // eslint-disable-next-line @typescript-eslint/camelcase
          session_id: sessionId,
          // eslint-disable-next-line @typescript-eslint/camelcase
          id_user: userId,
          // eslint-disable-next-line @typescript-eslint/camelcase
          id_program: programId,
          // eslint-disable-next-line @typescript-eslint/camelcase
          id_company: companyId,
          // eslint-disable-next-line @typescript-eslint/camelcase
          model_name: selectedModel,
          // eslint-disable-next-line @typescript-eslint/camelcase
          use_neighbors: useNeighbors,
          // eslint-disable-next-line @typescript-eslint/camelcase
          client_ctx: clientContextWithOCR,
          temperature: temperature,
          // eslint-disable-next-line @typescript-eslint/camelcase
          suppress_history: suppressHistory,
          // eslint-disable-next-line @typescript-eslint/camelcase
          tools_enabled: toolsEnabled,
          // eslint-disable-next-line @typescript-eslint/camelcase
          suppress_context: suppressContext,
          // eslint-disable-next-line @typescript-eslint/camelcase
          only_validated: onlyValidated,
        },
        controller.signal,
      );

      const answer = response.answer || 'No answer received.';
      const responseTime = response.responseTime || 0;
      const links = response.links || [];
      const previews = response.previews;
      const attachments = response.attachments;
      const chatHistoryId = response.chat_history_id;
      const walkthroughFlow = response.walkthrough_flows?.[0];
      const images = response.images;
      const audios = response.audios;

      dispatch(
        actions.chatbot.updateLastMessageWithAttr({
          answer,
          responseTime,
          links,
          previews,
          attachments,
          chatHistoryId,
          walkthroughFlow,
          images,
          audios,
        }),
      );
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        return;
      }

      if (axios.isCancel(e)) {
        return;
      }

      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      console.error('Request failed with error:', errorMessage);
      dispatch(actions.chatbot.updateLastMessage('Oops, there is an error. Please retry'));
    } finally {
      abortControllerRef.current = null;
      dispatch(actions.chatbot.setLoading(false));
    }
  };

  const sendFeedback = async ({
    rating,
    message,
    chatHistoryId,
  }: {
    rating?: number;
    message?: string;
    chatHistoryId: string;
  }) => {
    const feedbackData = {
      // eslint-disable-next-line @typescript-eslint/camelcase
      session_id: sessionId,
      // eslint-disable-next-line @typescript-eslint/camelcase
      id_user: userId,
      // eslint-disable-next-line @typescript-eslint/camelcase
      id_program: programId,
      // eslint-disable-next-line @typescript-eslint/camelcase
      chat_history_id: chatHistoryId,
      rating: rating,
      comment: message,
      metadata: {
        browser: navigator.userAgent,
        model: selectedModel,
      },
    };
    try {
      await chatbotService.sendFeedback(feedbackData);
      if (rating === 1) {
        dispatch(actions.chatbot.setFeedbackState({ chatHistoryId, feedbackType: 'up' }));
      }
      if (rating === -1) {
        dispatch(actions.chatbot.setFeedbackState({ chatHistoryId, feedbackType: 'down' }));
      }
      if (message) {
        dispatch(actions.chatbot.setFeedbackState({ chatHistoryId, feedbackType: 'flag' }));
      }
    } catch (error) {
      console.error('Failed to send feedback:', error);
    }
  };

  return {
    loading,
    messages,
    historyMessages,
    ask,
    clearMessages,
    isShowen,
    mode,
    close,
    selectedModel,
    setSelectedModel,
    models,
    useNeighbors,
    setUseNeighbors,
    temperature,
    setTemperature,
    suppressHistory,
    setSuppressHistory,
    toolsEnabled,
    setToolsEnabled,
    suppressContext,
    setSuppressContext,
    onlyValidated,
    setOnlyValidated,
    tools,
    abort,
    hasChatbotAdvancedConfig,
    isCheckingPrivileges,
    fetchInitialData,
    checkPrivileges,
    sendFeedback,
    feedbackStates,
  };
};

export default useChatbot;
